import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  AutocompleteSearch,
  IconTooltip,
  InputSearch,
  RadioButtonsGroup,
  Selector,
  type SelectorOptionPropsInterface
} from 'sonata-react-components';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getRetargetingCampaigns } from '@/resources/audienceTargeting.resources';
import { getRetargetingEventLabel } from './getRetargetingEventLabel.helper';

import type { RetargetingSegmentType } from 'sonata-static-storage/lib/schemas/targeting/Audience/Audience.types';

import './retargetingShortcut.scss';

const NO = 'no';
const YES = 'yes';

type FieldLabelProps = {
  htmlFor: string;
  label: string;
  tooltip: string;
};

function FieldLabel({ htmlFor, label, tooltip }: FieldLabelProps) {
  return (
    <div className="retargetingShortcut__label">
      <label htmlFor={htmlFor}>{label}</label>
      <IconTooltip icon="fa-regular fa-circle-info" iconColor="var(--color-sonata-primary-500) !important" place="top">
        {tooltip}
      </IconTooltip>
    </div>
  );
}

type CampaignSuggestion = {
  events: string[];
  id: string;
  label: string;
  userName?: string;
};

export type RetargetingSelection = Omit<RetargetingSegmentType, 'listId'>;

type Props = {
  onChange: (selection: RetargetingSelection | null) => void;
  segment?: RetargetingSegmentType | null;
};

function RetargetingShortcut({ onChange, segment }: Props) {
  const { formatMessage } = useIntl();
  const { user } = useTargetingContext();
  const token: Record<string, any> = {};

  const [enabled, setEnabled] = useState(Boolean(segment));
  const [campaign, setCampaign] = useState<CampaignSuggestion | null>(
    segment ? { events: [segment.eventType], id: segment.campaignId, label: segment.campaignName } : null
  );
  const [eventType, setEventType] = useState<string | null>(segment?.eventType ?? null);
  const [resetKey, setResetKey] = useState(0);
  const hadSegment = useRef(Boolean(segment));

  useEffect(() => {
    if (segment) {
      fetchCampaigns(segment.campaignId).then(suggestions => {
        const restoredCampaign = suggestions.find(suggestion => suggestion.id === segment.campaignId);
        if (restoredCampaign) {
          setCampaign(prevCampaign => (prevCampaign?.id === restoredCampaign.id ? restoredCampaign : prevCampaign));
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!segment && hadSegment.current && eventType) {
      setEnabled(false);
      setCampaign(null);
      setEventType(null);
      setResetKey(prev => prev + 1);
    }
    hadSegment.current = Boolean(segment);
  }, [segment]);

  const fetchCampaigns = async (searchText: string): Promise<CampaignSuggestion[]> => {
    const response = await getRetargetingCampaigns(token, user, searchText);
    return (response?.items ?? []).map(item => ({
      events: item.events ?? [],
      id: item._id,
      label: item.name,
      userName: item.userName
    }));
  };

  const enabledItems = [
    { id: NO, text: formatMessage({ defaultMessage: 'No', id: 'targeting.audienceTargeting.retargetingShortcut.no' }) },
    {
      id: YES,
      text: formatMessage({ defaultMessage: 'Yes', id: 'targeting.audienceTargeting.retargetingShortcut.yes' })
    }
  ];

  const handleEnabledChange = (id: string) => {
    const checked = id === YES;
    setEnabled(checked);
    if (!checked) {
      setCampaign(null);
      setEventType(null);
      setResetKey(prev => prev + 1);
      onChange(null);
    }
  };

  const handleCampaignChange = (selected: CampaignSuggestion) => {
    setCampaign(selected);
    setEventType(null);
    onChange(null);
  };

  const handleEventChange = (option: SelectorOptionPropsInterface) => {
    if (!campaign) {
      return;
    }
    const nextEvent = String(option.id);
    setEventType(nextEvent);
    onChange({ campaignId: campaign.id, campaignName: campaign.label, eventType: nextEvent });
  };

  const eventOptions = (campaign?.events ?? []).map(event => ({
    id: event,
    label: getRetargetingEventLabel(formatMessage, event)
  }));
  const selectedEvent = eventOptions.filter(option => option.id === eventType);
  const campaignPlaceholder = formatMessage({
    defaultMessage: 'Search campaign or user...',
    id: 'targeting.audienceTargeting.retargetingShortcut.campaign.placeholder'
  });

  return (
    <div className="retargetingShortcut" id="retargetingShortcut">
      <div className="retargetingShortcut__header">
        <span className="retargetingShortcut__title">
          {formatMessage({
            defaultMessage: 'Retargeting',
            id: 'targeting.audienceTargeting.retargetingShortcut.title'
          })}
        </span>
        <span className="retargetingShortcut__description">
          {formatMessage({
            defaultMessage:
              'Retargeting allows you to show ads to users who have visited specific zones and performed a certain action. Will your campaign include retargeting?',
            id: 'targeting.audienceTargeting.retargetingShortcut.description'
          })}
        </span>
      </div>
      <RadioButtonsGroup
        className="retargetingShortcut__radioButtons"
        id="retargetingShortcut-enabled"
        items={enabledItems}
        onChange={handleEnabledChange}
        value={enabled ? YES : NO}
      />
      <div className="retargetingShortcut__fields">
        <div className="retargetingShortcut__field">
          <FieldLabel
            htmlFor="retargetingShortcut-campaign"
            label={formatMessage({
              defaultMessage: 'Select the base campaign you want to use for retargeting',
              id: 'targeting.audienceTargeting.retargetingShortcut.campaign.label'
            })}
            tooltip={formatMessage({
              defaultMessage: 'Only published campaigns can be used as the base for retargeting.',
              id: 'targeting.audienceTargeting.retargetingShortcut.campaign.tooltip'
            })}
          />
          {enabled ? (
            <AutocompleteSearch<CampaignSuggestion>
              fetchData={fetchCampaigns}
              id="retargetingShortcut-campaign"
              initialSuggestion={campaign ?? undefined}
              key={resetKey}
              onSelectionChange={handleCampaignChange}
              placeholder={campaignPlaceholder}
              showInputLens
            >
              {suggestions =>
                suggestions.map(suggestion => (
                  <AutocompleteSearch.Suggestion id={suggestion.id} key={suggestion.id}>
                    <span className="retargetingShortcut__suggestion">{suggestion.label}</span>
                    {suggestion.userName && (
                      <span className="retargetingShortcut__suggestionUser">{suggestion.userName}</span>
                    )}
                  </AutocompleteSearch.Suggestion>
                ))
              }
            </AutocompleteSearch>
          ) : (
            <InputSearch
              disabled
              id="retargetingShortcut-campaign-disabled"
              lens
              name="retargetingShortcut-campaign-disabled"
              placeholder={campaignPlaceholder}
            />
          )}
        </div>
        <div className="retargetingShortcut__field">
          <FieldLabel
            htmlFor="retargetingShortcut-event"
            label={formatMessage({
              defaultMessage: 'Select associated event',
              id: 'targeting.audienceTargeting.retargetingShortcut.event.label'
            })}
            tooltip={formatMessage({
              defaultMessage: 'Users who performed this event in the base campaign will be targeted.',
              id: 'targeting.audienceTargeting.retargetingShortcut.event.tooltip'
            })}
          />
          <Selector<SelectorOptionPropsInterface>
            id="retargetingShortcut-event"
            isDisabled={!enabled || !campaign}
            onChange={handleEventChange}
            options={eventOptions}
            placeholder={formatMessage({
              defaultMessage: 'Select...',
              id: 'targeting.audienceTargeting.retargetingShortcut.event.placeholder'
            })}
            type="simple"
            value={selectedEvent}
          />
        </div>
      </div>
    </div>
  );
}

export default RetargetingShortcut;
